import React, {useState, useEffect, useMemo} from "react";
import * as XLSX from "xlsx";
import axios from "../../axios"; // adjust path if needed

// ─── Generic Modal ───────────────────────────────────────────────────────────
const Modal = ({title, children, onClose, modalName = ""}) => (<div
    className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 pt-20 z-50"
    onClick={onClose}
    role="dialog"
    aria-modal="true"
>
    <div
        style={{maxHeight: "50rem", maxWidth: "75rem"}}
        className="bg-white w-full max-w-4xl max-h-[90vh] flex flex-col rounded-lg shadow-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
    >
        <header className="flex items-center justify-between px-5 py-3 border-b bg-gray-50 sticky top-0 z-10">
            <h2 className="text-lg font-semibold">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close">
                ✕
            </button>
        </header>
        <div
            className={`flex-1 p-5 space-y-4 ${modalName === "single_run_modal" ? "overflow-y-hidden pr-0" : "overflow-y-auto"}`}
        >
            {children}
        </div>
    </div>
</div>);

// ─── Multi-Run Modal ─────────────────────────────────────────────────────────
function DateRunsModal({runs, baseUrl, onClose, parseStep, formatStatusText, setZoomedImageUrl}) {
    const [openIndex, setOpenIndex] = useState(null);

    return (<Modal title={`Runs on Day ${new Date(runs[0].execution_time).getDate()}`} onClose={onClose}>
        <div className="space-y-4">
            {runs.map((run, idx) => {
                const isOpen = idx === openIndex;
                return (<div key={idx} className="bg-gray-50 rounded-lg overflow-hidden shadow-sm">
                    <button
                        onClick={() => setOpenIndex(isOpen ? null : idx)}
                        style={{textAlign: "left"}}
                        className="w-full flex items-center gap-4 p-4 hover:bg-gray-100 transition"
                    >
                        {run.screenshot_url && (<img
                            src={`${baseUrl}/${run.screenshot_url}`}
                            alt=""
                            className="w-12 h-12 object-cover rounded cursor-pointer flex-none"
                            onClick={(e) => {
                                e.stopPropagation();
                                setZoomedImageUrl(`${baseUrl}/${run.screenshot_url}`);
                            }}
                        />)}
                        <span className="flex-1 truncate" title={run.scenario_name}>
                  {run.scenario_name}
                </span>
                        <span
                            className={`whitespace-nowrap ${run.status.toLowerCase() === "passed" ? "text-green-700 font-bold" : run.status.toLowerCase() === "failed" ? "text-red-700 font-bold" : "text-gray-700 font-bold"}`}
                        >
                  {formatStatusText(run.status)}
                </span>
                        <svg
                            className={`w-5 h-5 text-gray-400 transform transition ${isOpen ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M19 9l-7 7-7-7"/>
                        </svg>
                    </button>
                    {isOpen && (<div className="p-4 border-t border-gray-200 space-y-4">
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 text-sm">
                            <div>
                                <dt className="font-medium inline">Time:</dt>
                                {" "}
                                <dd className="inline">{run.execution_time}</dd>
                            </div>
                            <div>
                                <dt className="font-medium inline">Device:</dt>
                                {" "}
                                <dd className="inline">{run.browser_or_device}</dd>
                            </div>
                            <div className="sm:col-span-2">
                                <dt className="font-medium inline">By:</dt>
                                {" "}
                                <dd className="inline">{run.tester_name}</dd>
                            </div>
                        </dl>
                        <h4 className="font-medium">Steps & Screenshots</h4>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {run.steps.map((step, j) => {
                                const {keyword, text} = parseStep(step);
                                const thumb = run.stepScreenshots?.[j];
                                const src = thumb?.startsWith("http") ? thumb : `${baseUrl}/${thumb}`;
                                return (<li key={j}
                                            className="bg-white rounded-lg shadow p-2 flex flex-col items-center">
                                    {thumb ? (<img
                                        src={src}
                                        alt=""
                                        className="w-full h-32 object-cover rounded cursor-pointer"
                                        onClick={() => setZoomedImageUrl(src)}
                                    />) : (<div
                                        className="w-full h-32 bg-gray-100 flex items-center justify-center text-gray-400">
                                        No Image
                                    </div>)}
                                    <p className="mt-2 text-sm text-center">
                                        {keyword && <strong>{keyword}:</strong>} {text}
                                    </p>
                                </li>);
                            })}
                        </ul>
                    </div>)}
                </div>);
            })}
        </div>
    </Modal>);
}

// ─── Main Table Component ────────────────────────────────────────────────────
export default function ReportTable({project, feature}) {
    const PAGE_SIZE = 50;
    const baseUrl = process.env.REACT_APP_API_URL.replace("/api", "");

    // fetched data
    const [results, setResults] = useState([]);
    const [scenarioOrder, setScenarioOrder] = useState([]);
    const [totalPages, setTotalPages] = useState(1);

    // UI state
    const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [showGroupingInputs, setShowGroupingInputs] = useState(false);
    const [groupScenarioName, setGroupScenarioName] = useState("");
    const [groupJobName, setGroupJobName] = useState("");
    const [groupingName, setGroupingName] = useState("");
    const [screenshotModalData, setScreenshotModalData] = useState(null);
    const [detailsModalData, setDetailsModalData] = useState(null);
    const [zoomedImageUrl, setZoomedImageUrl] = useState(null);

    useEffect(() => {
        if (!project || !feature) return;

        axios
            .get("/automation-result", {
                params: {
                    project, feature, date: selectedMonth, page: currentPage
                }
            })
            .then(response => {
                const hdr = response.headers["x-scenario-order"];
                const order = hdr ? JSON.parse(hdr) : [];
                setScenarioOrder(order);
                setResults(response.data.results);
                setTotalPages(response.data.totalPages);
            })
            .catch(err => {
                console.error(err);
                setResults([]);
                setTotalPages(1);
            });
    }, [project, feature, selectedMonth, currentPage]);

    //Step Helpers
    const parseStep = (step) => {
        const m = step.match(/^(Given|When|Then|And)\s+(.*)$/i);
        return m ? {keyword: m[1], text: m[2]} : {keyword: "", text: step};
    };
    const formatStatusText = (raw) => {
        const s = (raw || "").toLowerCase();
        if (s === "passed") return "Passed";
        if (s === "failed") return "Failed";
        if (s === "n/a") return "N/A";
        return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    };
    const getColor = (status) => {
        const s = (status || "").toLowerCase();
        if (s === "passed") return "bg-green-700 text-white";
        if (s === "failed") return "bg-red-600 text-white";
        if (s === "n/a") return "bg-yellow-500 text-white";
        return "bg-blue-200 text-blue-700";
    };

    // grouping API
    const handleSaveGroup = async () => {
        if (!groupScenarioName || !groupJobName || !groupingName) {
            alert("Scenario, Job & Grouping are required.");
            return;
        }
        try {
            const {data} = await axios.put("/automation-result/grouping", {
                scenario_name: groupScenarioName, job_name: groupJobName, grouping_name: groupingName
            });
            alert(data.message);
            setShowGroupingInputs(false);
            setGroupScenarioName("");
            setGroupJobName("");
            setGroupingName("");
        } catch (err) {
            alert(err.response?.data?.message || err.message);
        }
    };

    // normalize steps/screenshots
    const normalized = useMemo(() => results.map((r) => {
        let steps = Array.isArray(r.steps) ? r.steps : [];
        if (typeof r.steps === "string") {
            try {
                const p = JSON.parse(r.steps);
                if (Array.isArray(p)) steps = p;
            } catch {
            }
        }
        let stepScreenshots = Array.isArray(r.step_screenshots) ? r.step_screenshots : [];
        if (typeof r.step_screenshots === "string") {
            try {
                const arr = JSON.parse(r.step_screenshots);
                if (Array.isArray(arr)) stepScreenshots = arr;
            } catch {
            }
        }
        return {...r, steps, stepScreenshots};
    }), [results]);

    // filter by month
    const filtered = useMemo(() => {
        const [Y, M] = selectedMonth.split("-").map(Number);
        return normalized.filter((r) => {
            const dt = new Date(r.execution_time);
            return dt.getFullYear() === Y && dt.getMonth() + 1 === M;
        });
    }, [normalized, selectedMonth]);

    // grouping & job maps
    const groupingMap = useMemo(() => normalized.reduce((m, r) => {
        if (r.grouping_name) m[r.scenario_name] = r.grouping_name;
        return m;
    }, {}), [normalized]);
    const jobMap = useMemo(() => normalized.reduce((m, r) => {
        if (r.job_name) m[r.scenario_name] = r.job_name;
        return m;
    }, {}), [normalized]);

    // group by day
    const grouped = useMemo(() => {
        return filtered.reduce((acc, r) => {
            const d = new Date(r.execution_time).getDate();
            acc[r.scenario_name] = acc[r.scenario_name] || {};
            acc[r.scenario_name][d] = acc[r.scenario_name][d] || [];
            acc[r.scenario_name][d].push(r);
            return acc;
        }, {});
    }, [filtered]);
    const dayDetails = useMemo(() => {
        return filtered.reduce((acc, r) => {
            const d = new Date(r.execution_time).getDate();
            acc[d] = acc[d] || [];
            acc[d].push(r);
            return acc;
        }, {});
    }, [filtered]);

    // day headers
    const [year, month] = selectedMonth.split("-").map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    const dayHeaders = Array.from({length: daysInMonth}, (_, i) => i + 1);

    // order scenarios
    const orderedScenarios = useMemo(() => {
        const all = Object.keys(grouped);
        const earliest = {};
        filtered.forEach((r) => {
            const ts = new Date(r.execution_time).getTime();
            if (!earliest[r.scenario_name] || ts < earliest[r.scenario_name]) earliest[r.scenario_name] = ts;
        });
        const inOrder = scenarioOrder.filter((s) => all.includes(s));
        const remainder = all
            .filter((s) => !scenarioOrder.includes(s))
            .sort((a, b) => (earliest[a] || Infinity) - (earliest[b] || Infinity));
        return [...inOrder, ...remainder].filter((s) => s.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [grouped, scenarioOrder, filtered, searchTerm]);

    // pagination
    const pagedScenarios = orderedScenarios.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    // open date modal
    const openDateModal = (day) => {
        const runs = dayDetails[day] || [];
        const sorted = runs.slice().sort((a, b) => new Date(b.execution_time) - new Date(a.execution_time));
        setDetailsModalData(sorted);
    };

    const formattedMonth = useMemo(() => {
        const [year, month] = selectedMonth.split("-").map(Number);
        const dt = new Date(year, month - 1, 1);
        // e.g. "June 2025"
        return dt.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    }, [selectedMonth]);

    return (<>
        {/* === Grouping UI === */}
        <div className="flex items-center gap-2 mb-4">
            <button
                onClick={() => setShowGroupingInputs((v) => !v)}
                className="bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700"
            >
                Add Group
            </button>
            {showGroupingInputs && (<>
                <input
                    type="text"
                    placeholder="Scenario Name"
                    value={groupScenarioName}
                    onChange={(e) => setGroupScenarioName(e.target.value)}
                    className="border px-2 py-1 rounded"
                />
                <input
                    type="text"
                    placeholder="Job Name"
                    value={groupJobName}
                    onChange={(e) => setGroupJobName(e.target.value)}
                    className="border px-2 py-1 rounded"
                />
                <input
                    type="text"
                    placeholder="Grouping Name"
                    value={groupingName}
                    onChange={(e) => setGroupingName(e.target.value)}
                    className="border px-2 py-1 rounded"
                />
                <button
                    onClick={handleSaveGroup}
                    disabled={!groupScenarioName || !groupJobName || !groupingName}
                    className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700 disabled:opacity-50"
                >
                    Save
                </button>
            </>)}
        </div>

        {/* === Filters & Export === */}
        <div className="flex flex-col sm:flex-row items-center gap-4 mb-4">
            <label>
                Month:&nbsp;
                <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => {
                        setSelectedMonth(e.target.value);
                        setCurrentPage(1);
                    }}
                    className="border px-2 py-1 rounded"
                />
            </label>
            <label>
                Search:&nbsp;
                <input
                    type="text"
                    placeholder="Scenario…"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    className="border px-2 py-1 rounded"
                />
            </label>
            <button
                onClick={() => {
                    const sheet = XLSX.utils.json_to_sheet(Object.entries(grouped).map(([sc, daily]) => {
                        const row = {Scenario: sc};
                        dayHeaders.forEach((d) => {
                            const runs = daily[d] || [];
                            row[d] = runs.length ? runs.map((r) => r.status.charAt(0).toUpperCase()).join(",") : "-";
                        });
                        return row;
                    }));
                    const wb = XLSX.utils.book_new();
                    XLSX.utils.book_append_sheet(wb, sheet, "Results");
                    XLSX.writeFile(wb, `Automation_${selectedMonth}.xlsx`);
                }}
                className="bg-green-600 text-white px-4 py-1 rounded hover:bg-green-700"
            >
                Export to Excel
            </button>
        </div>

        {/* === No-data or Table === */}
        {filtered.length === 0 ? (<div className="mt-8 p-6 text-center text-gray-600">
            No automation runs available for <strong>{formattedMonth}</strong>.<br/>
            Please pick another month above.
        </div>) : (<>
            {/* === Main Table === */}
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left border-collapse">
                    <thead className="bg-indigo-100 text-gray-700">
                    <tr>
                        <th className="px-2 py-1 sticky left-0 bg-indigo-100 z-20 text-center w-10">S.No</th>
                        <th className="px-2 py-1 sticky left-10 bg-indigo-100 z-10">Scenario</th>
                        {dayHeaders.map((d) => {
                            const hasRuns = Array.isArray(dayDetails[d]) && dayDetails[d].length > 0;
                            return (<th
                                key={d}
                                onClick={() => hasRuns && openDateModal(d)}
                                className={`px-2 py-1 text-center text-xs ${hasRuns ? "cursor-pointer hover:bg-gray-100" : "cursor-not-allowed text-gray-400"}`}
                                title={hasRuns ? "View all runs" : "No runs"}
                            >
                                {d}
                            </th>);
                        })}
                    </tr>
                    </thead>
                    <tbody>
                    {pagedScenarios.map((scenario, idx) => {
                        const globalIndex = (currentPage - 1) * PAGE_SIZE + idx + 1;
                        const daily = grouped[scenario] || {};
                        const groupName = groupingMap[scenario];
                        const jobName = jobMap[scenario];
                        const rowClass = groupName ? "bg-gray-100 hover:bg-gray-200" : idx % 2 === 0 ? "bg-gray-50 hover:bg-gray-100" : "bg-white hover:bg-gray-50";

                        return (<tr key={scenario} className={rowClass}>
                            <td className="px-2 py-1 sticky left-0 bg-indigo-50 z-20 text-center w-10">{globalIndex}</td>
                            <td className="px-2 py-1 sticky left-10 bg-indigo-50 z-10">
                                <div className="flex items-center space-x-2">
                          <span className={groupName ? "font-bold text-blue-900 border-l-4 border-blue-500 pl-2" : ""}>
                            {scenario}
                          </span>
                                    {groupName && jobName && (<button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            axios
                                                .post("/jenkins/execute", {
                                                    jobName, tag: groupName
                                                }, {headers: {"Content-Type": "application/json"}})
                                                .then(() => alert(`Triggered Jenkins job '${jobName}'.`))
                                                .catch((err) => alert("Jenkins trigger failed: " + (err.response?.data?.message || err.message)));
                                        }}
                                        title={`Run group: ${groupName}`}
                                        className="ml-2 p-2 bg-indigo-600 rounded hover:bg-indigo-800 text-white"
                                    >
                                        ▶
                                    </button>)}
                                    {jobName && !groupName &&
                                        <span className="text-xs italic text-gray-600">({jobName})</span>}
                                </div>
                            </td>

                            {dayHeaders.map((d) => {
                                const runs = daily[d] || [];
                                if (!runs.length) {
                                    return (<td key={d}
                                                className="px-2 py-1 text-center text-xs bg-gray-100 text-gray-400">-</td>);
                                }
                                const sorted = runs
                                    .slice()
                                    .sort((a, b) => new Date(b.execution_time) - new Date(a.execution_time));
                                const latest = sorted[0];
                                const count = sorted.length;
                                const now = Date.now();
                                const recent = sorted
                                    .slice(1)
                                    .some((r) => now - new Date(r.execution_time).getTime() < 3 * 60 * 60 * 1000);

                                return (<td
                                    key={d}
                                    className={`${getColor(latest.status)} relative px-2 py-1 text-center text-xs cursor-pointer ${count > 1 ? "hover:bg-opacity-90" : ""}`}
                                    onClick={() => count === 1 ? setScreenshotModalData({
                                        ...latest, day: d
                                    }) : setDetailsModalData(sorted)}

                                    title={count === 1 ? `#${latest.build_number} → ${formatStatusText(latest.status)}` : `#${latest.build_number} → ${formatStatusText(latest.status)} (+${count - 1})`}
                                >
                            <span className="font-bold text-white text-sm">
                              {formatStatusText(latest.status).charAt(0)}
                            </span>
                                    {count > 1 && (<div
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDetailsModalData(sorted);
                                        }}
                                        className={`absolute top-0 right-0 -translate-x-1/2 -translate-y-1/2 bg-blue-600 text-white text-[8px] font-bold rounded-full w-5 h-5 flex items-center justify-center ${recent ? "ring-2 ring-yellow-400 animate-pulse" : ""}`}
                                    >
                                        +{count - 1}
                                    </div>)}
                                </td>);
                            })}
                        </tr>);
                    })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="flex justify-between items-center mt-4">
                <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                >Previous
                </button>
                <span className="text-sm">Page {currentPage} of {totalPages}</span>
                <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                >Next
                </button>
            </div>
        </>)}

        {/* Detail Modals */}
        {detailsModalData && Array.isArray(detailsModalData) && detailsModalData.length > 1 && (<DateRunsModal
            runs={detailsModalData}
            baseUrl={baseUrl}
            onClose={() => setDetailsModalData(null)}
            parseStep={parseStep}
            formatStatusText={formatStatusText}
            setZoomedImageUrl={setZoomedImageUrl}
        />)}
        {screenshotModalData && !Array.isArray(screenshotModalData) && (<Modal
            title={`${screenshotModalData.scenario_name} — Day ${screenshotModalData.day}`}
            onClose={() => setScreenshotModalData(null)}
            modalName="single_run_modal"
        >
            <div className="flex">
                {screenshotModalData.screenshot_url && (<img
                    src={`${baseUrl}/${screenshotModalData.screenshot_url}`}
                    alt="Full scenario screenshot"
                    className="w-full max-h-[60vh] rounded-lg cursor-pointer"
                    onClick={() => setZoomedImageUrl(`${baseUrl}/${screenshotModalData.screenshot_url}`)}
                />)}
                <div className="ml-6 mt-0">
                    <h3 className="text-lg font-medium mb-4">Steps & Screenshots</h3>
                    <ul className="space-y-4 overflow-y-auto" style={{height: "42rem", width: "55rem"}}>
                        {screenshotModalData.steps.map((step, i) => {
                            const {keyword, text} = parseStep(step);
                            const thumbPath = screenshotModalData.stepScreenshots[i];
                            const thumbSrc = thumbPath ? thumbPath.startsWith("http") ? thumbPath : `${baseUrl}/${thumbPath}` : "https://via.placeholder.com/100?text=no+image";
                            return (<li key={i} className="flex items-start gap-4">
                                <img
                                    src={thumbSrc}
                                    alt={`Step ${i + 1}`}
                                    className="w-20 h-20 object-cover rounded-md shadow-sm cursor-pointer flex-none"
                                    onClick={() => setZoomedImageUrl(thumbSrc)}
                                />
                                <div>
                                    {keyword && <span className="font-medium">{keyword}:</span>}
                                    <span>{text}</span>
                                </div>
                            </li>);
                        })}
                    </ul>
                </div>
            </div>
        </Modal>)}
        {zoomedImageUrl && (<Modal title="" onClose={() => setZoomedImageUrl(null)} modalName="zoomModal">
            <div className="flex justify-center">
                <img
                    src={zoomedImageUrl}
                    alt="Full-screen"
                    className="max-w-[90vw] max-h-[90vh] object-contain"
                />
            </div>
        </Modal>)}
    </>);
}
